import cuid from "cuid";
import { get, merge } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOutlineAutoFixHigh } from "react-icons/md";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { RELATIONSHIP } from "utils/constants";
import { buildPatch } from "./write";

const isDev =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname.includes("dev"));

const TEST_DATA = {
  "user:gender": "M",
  "user:lastname": "Dupont",
  "user:firstname": "Jean",
  "user:birthDate": "1990-01-15T00:00:00.000Z",
  "contact:socialRegime": "SECURITE_SOCIALE",
  "contact:socialSecurityNumber": "1900175001234 56",
  "contact:profession": "Ingénieur informatique",
  "contact:exerciseFrame": "SALARIE",
  "contact:politicallyExposedPerson": false,
  "contact:acceptContact": true,
  "contact:acceptDigital": "true",
  "contact:fields.familySituation": "MARIE",
  "contact:fields.birthName": "Martin",
  "contact:fields.birthCountry": "FRANCE",
  "contact:fields.birthDepartment": "75",
  "contact:fields.professionalSituation": "SALARIE",
  "contact:fields.ppeRelation": false,
  "telecom:value:PHONE": "0612345678",
  "telecom:value:LANDLINE": "0145678901",
  "telecom:value:EMAIL": "jean.dupont@test.fr",
  "telecom:value:ZIP_CODE": "75001",
  "telecom:value:CITY": "Paris",
  "telecom:value:ADDRESS": "12 rue de Rivoli",
  "telecom:value:COMPLEMENT": "Apt 4B",
  "contract:issuanceDate": new Date(
    Date.now() + 30 * 24 * 60 * 60 * 1000,
  ).toISOString(),
  "project:ria": false,
  "project:fields.date_effet": new Date(
    Date.now() + 14 * 24 * 60 * 60 * 1000,
  ).toISOString(),
  "project:fields.statut_mp": "MILITAIRE_ACTIVE",
  "project:fields.regime": "CNMSS",
  "project:fields.sib": "2450",
  "project:fields.grade": "Adjudant",
  "project:fields.perte_solde_active": true,
  "project:fields.rente_education_active": true,
  "project:fields.a_autre_prevoyance": "non",
  "project:fields.dependance_mp_active": true,
  "project:fields.deces_mp_active": true,
  "project:fields.avq_declaration_mp": false,
  "project:fields.a_conjoint": "oui",
  "project:fields.formule_selectionnee": "FORMULE_2",
  "project:fields.dependance_optional": false,
  "project:fields.deces_optional": false,
  "project:fields.consent_dda": true,
  "project:fields.consent_cg": true,
  "contract:caseType": "NEW_CONTRACT",
  "contract:saleMode": "FACE_TO_FACE",
  "project:projectType": "HOT",
};

const PROJECT_TARGETS = new Set([
  "project",
  "user",
  "contact",
  "telecom",
  "contract",
  "spouse",
  "children",
]);

const buildKey = (target) => {
  const { entity, property, filterValue } = target;
  if (entity === "telecom") return `telecom:${property}:${filterValue}`;
  return `${entity}:${property}`;
};

const slotsFromStatus = (currentStatus) => {
  const screen = (currentStatus?.screens || []).find(
    (s) => s.currentPublishedVersion,
  );
  return screen?.currentPublishedVersion?.bindingManifest?.slots || [];
};

const withEnv = (WrappedComponent) => {
  const EnvWrapper = (props) => {
    const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
    const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT, {
      onCompleted: () => window.location.reload(),
    });

    if (!isDev) return <WrappedComponent {...props} />;

    const fill = () => {
      const { project, currentStatus } = props;
      if (!project) return;
      const slots = slotsFromStatus(currentStatus);
      if (!slots.length) return;

      let projectPatch = { id: project.id };
      let currentProject = { ...project };

      for (const slot of slots) {
        if (slot.binding?.kind !== "entity_field") continue;
        const target = {
          entity: slot.binding.entity,
          property: slot.binding.field,
        };
        if (!PROJECT_TARGETS.has(target.entity)) continue;

        const value = TEST_DATA[buildKey(target)];
        if (value === undefined) continue;

        const patch = buildPatch(target, value, currentProject, "project");
        projectPatch = merge(projectPatch, patch);
        currentProject = merge({}, currentProject, patch);
      }

      const entityField = (entity) => (s) =>
        s.binding?.kind === "entity_field" && s.binding?.entity === entity;
      const hasSpouseFields = slots.some(entityField("spouse"));
      const hasChildFields = slots.some(entityField("children"));

      if (hasSpouseFields || hasChildFields) {
        const persons = [
          ...(get(currentProject, "contact.relatedPersons") || []),
        ];
        if (
          hasSpouseFields &&
          !persons.some((p) => p.relationship === RELATIONSHIP.SPOUSE)
        ) {
          persons.push({
            id: cuid(),
            relationship: RELATIONSHIP.SPOUSE,
            identity: {
              id: cuid(),
              socialRegime: "SECURITE_SOCIALE",
              user: {
                id: cuid(),
                gender: "F",
                firstname: "Marie",
                lastname: "Dupont",
                birthDate: "1992-03-20T00:00:00.000Z",
              },
            },
          });
        }
        if (
          hasChildFields &&
          !persons.some((p) => p.relationship === RELATIONSHIP.CHILD)
        ) {
          persons.push({
            id: cuid(),
            relationship: RELATIONSHIP.CHILD,
            identity: {
              id: cuid(),
              user: {
                id: cuid(),
                gender: "M",
                firstname: "Lucas",
                lastname: "Dupont",
                birthDate: "2018-07-10T00:00:00.000Z",
              },
            },
          });
        }
        projectPatch = merge(projectPatch, {
          contact: {
            id: get(project, "contact.id"),
            relatedPersons: persons,
          },
        });
      }

      updateProject({
        variables: { where: { id: project.id }, data: projectPatch },
      });
    };

    return (
      <div className="canvas-sandbox">
        <button type="button" className="canvas-sandbox__fill" onClick={fill}>
          <MdOutlineAutoFixHigh size={14} />
          {t("RUN.SANDBOX_FILL")}
        </button>
        <WrappedComponent {...props} />
      </div>
    );
  };

  return EnvWrapper;
};

export default withEnv;
